import { IconButton, Box } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';

import React, { useState } from 'react';

import { useQuery, useMutation } from '@apollo/client';

import { format } from 'date-fns';
import { GraphQLFormattedError } from 'graphql';

import { RootState } from '@/store';
import { Close } from '@/icons';
import EditPatientForm from '@/components/Form/EditPatient';

import usePaginatedPatients from '@/hooks/apollo/queries/usePaginatedPatients';
import { EditPatientFormType } from '@/types';
import { UPDATE_PATIENT } from '@/apollo/mutations';
import { CARETEAM_QUERY, PRACTITIONER_INFO_QUERY } from '@/apollo/queries';

import { PDT_UID } from '@/utils/constants';
import { DrawerPayloadType } from '@/types/ReduxTypes';
import { actions as toastActions } from '@/store/slices/toast';

import { useModal } from '@/hooks/redux';

type Props = {
  payload: DrawerPayloadType['EDIT_PATIENT'];
  onClose: () => void;
};

function EditPatientDrawer(props: Props) {
  const { payload, onClose } = props;
  const dispatch = useDispatch();
  const careteamUid = payload.patientId;
  const {
    data: careteamData,
    refetch: refetchCareteam,
  } = useQuery(CARETEAM_QUERY, {
    variables: { careteamUid },
    skip: !careteamUid,
  });
  const allowedPDTNames = useSelector((state: RootState) => state.providerUser.AllowedPDTNames);
  const pdtUids = allowedPDTNames?.map((pdt) => pdt.uid);
  const { refetch } = usePaginatedPatients(pdtUids);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const { data: practitionerData } = useQuery(PRACTITIONER_INFO_QUERY);
  const [updatePatientMutation] = useMutation(UPDATE_PATIENT);
  const [errors, setErrors] = useState<readonly GraphQLFormattedError[] | undefined>();
  const isoDate = careteamData?.careteam.patient.user.dateOfBirth;

  const [isFormDirty, setIsFormDirty] = useState(false);

  const { showModal, hideModal } = useModal();

  const defaultValues: EditPatientFormType = {
    email: careteamData?.careteam.patient.user.email || '',
    firstName: careteamData?.careteam.patient.user.firstName || '',
    lastName: careteamData?.careteam.patient.user.lastName || '',
    phone: careteamData?.careteam.patient.user.phone || '',
    dateOfBirth: isoDate ? format(new Date(isoDate), 'MM/dd/yyyy') : '',
    pdtUid: careteamData?.careteam.pdt.uid || PDT_UID,
  };

  const handleSubmit = async (formData: EditPatientFormType) => {
    const {
      email,
      firstName,
      lastName,
      phone,
      dateOfBirth,
    } = formData;
    if (!careteamData?.careteam.patient.uid) {
      setErrors([{ message: 'Patient data not loaded' }]);
      return;
    }
    try {
      const { data, errors: resultErrors } = await updatePatientMutation({
        variables: {
          patientUid: careteamData?.careteam.patient.uid,
          changeReason: 'Updated patient info',
          email,
          firstName,
          lastName,
          phone: phone || null,
          dateOfBirth: new Date(dateOfBirth),
        },
      });
      if (resultErrors?.length) {
        setErrors(resultErrors);
        return;
      }
      if (data?.editPatientInfo?.success) {
        await refetchCareteam();
        setSubmitSuccess(true);
        refetch();
        dispatch(toastActions.showToast({
          payload: {
            title: 'Update Success',
            message: 'Patient information updated successfully',
            severity: 'success',
          },
        }));
        onClose();
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error occurred';
      setErrors([{ message }]);
    }
  };

  const handleCloseClick = () => {
    if (isFormDirty) {
      showModal('CONFIRMATION_POPUP', {
        header: 'Unsaved Changes',
        body: 'You have unsaved changes. Are you sure you want to leave?',
        confirmButtonText: 'Discard changes',
        cancelButtonText: 'Keep editing',
        onConfirm: () => {
          hideModal();
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  return (
    <Box width="min(420px, 100vw)">
      <Box width="48px" height="48px" style={{ float: 'right' }}>
        <IconButton aria-label="close drawer" onClick={handleCloseClick} size="large">
          <Close width="24px" height="24px" />
        </IconButton>
      </Box>
      <Box padding="40px">
        {careteamData && (
          <EditPatientForm
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            errors={errors}
            submitSuccess={submitSuccess}
            pdts={practitionerData?.practitioner?.accessCodes?.map(
              (accessCode) => accessCode.pdt,
            ) || []}
            onDirtyChange={setIsFormDirty}
          />
        )}
      </Box>
    </Box>
  );
}

export default EditPatientDrawer;
